import {
    useEffect,
    useState,
} from "react";

import {
    Alert,
    Box,
    Divider,
    LinearProgress,
    Paper,
    Typography,
} from "@mui/material";

import PersonOutlineIcon
    from "@mui/icons-material/PersonOutline";

import {
    useParams,
} from "react-router-dom";

import {
    PRIMARY_COLOR,
} from "../util/constants";

import {
    handleResponse,
} from "../util/integration";

import {
    fetchSellerImprintAPI,
} from "../api/imprint_api";

const LABEL = Object.freeze({
    title: "Impressum",

    subtitle:
        "Anbieter-Impressum",
});

const MESSAGE = Object.freeze({
    noImprint:
        "Für diesen Anbieter wurde noch kein Impressum hinterlegt.",

    loadError:
        "Das Anbieter-Impressum konnte nicht geladen werden.",

    missingUserId:
        "Es wurde keine Benutzer-ID angegeben.",
});

const CONFIG = Object.freeze({
    topSpacing: "50px",

    contentPadding: {
        xs: 3,
        sm: 4,
        md: 5,
    },
});

const STYLE = Object.freeze({
    paper: {
        width: "100%",

        mt: CONFIG.topSpacing,

        overflow: "hidden",

        backgroundColor:
            "common.white",

        borderRadius: 2,

        border: 1,

        borderColor:
            "divider",

        boxShadow:
            "0 6px 24px rgba(15, 23, 42, 0.08)",
    },

    content: {
        p: CONFIG.contentPadding,
    },

    heading: {
        display: "flex",

        alignItems:
            "flex-start",

        gap: 2,
    },

    headingText: {
        minWidth: 0,
    },

    subtitle: {
        mt: 0.75,
        mb: 0,

        color:
            "text.secondary",
    },

    divider: {
        my: {
            xs: 3,
            md: 4,
        },
    },

    imprintText: {
        color:
            "text.primary",

        fontFamily:
            "Arial, Helvetica, sans-serif",

        fontSize: {
            xs: "0.95rem",
            sm: "1rem",
        },

        lineHeight: 1.75,

        /*
         * Dadurch bleiben die aus "$"
         * erzeugten Zeilenumbrüche sichtbar.
         */
        whiteSpace:
            "pre-wrap",

        overflowWrap:
            "anywhere",

        wordBreak:
            "break-word",
    },

    emptyText: {
        color:
            "text.secondary",

        fontStyle:
            "italic",
    },
});

function createColorStyles(
    primaryColor,
) {
    return Object.freeze({
        titleIcon: {
            flexShrink: 0,

            mt: 0.25,

            fontSize: 38,

            color:
                primaryColor,
        },

        title: {
            color:
                primaryColor,

            fontWeight: 500,

            lineHeight: 1.2,
        },

        progress: {
            backgroundColor:
                "rgba(0, 0, 0, 0.06)",

            "& .MuiLinearProgress-bar": {
                backgroundColor:
                    primaryColor,
            },
        },
    });
}

function formatImprintText(
    imprint,
) {
    if (
        typeof imprint
        !== "string"
    ) {
        return "";
    }

    /*
     * Das Backend verwendet "$" als
     * Platzhalter für Zeilenumbrüche.
     */
    return imprint
        .replace(
            /\$/g,
            "\n",
        )
        .trim();
}

function normalizeImprint(
    response,
) {
    /*
     * Der aktuelle Endpoint liefert
     * direkt einen String.
     */
    if (
        typeof response
        === "string"
    ) {
        return formatImprintText(
            response,
        );
    }

    /*
     * Fallback für eine mögliche spätere
     * JSON-Antwort:
     *
     * {
     *     "imprint": "..."
     * }
     */
    if (
        response !== null
        && typeof response
            === "object"
        && typeof response.imprint
            === "string"
    ) {
        return formatImprintText(
            response.imprint,
        );
    }

    return "";
}

export default function UserImprint({
    appAPI,
}) {
    const {
        userId,
    } = useParams();

    const [
        imprint,
        setImprint,
    ] = useState("");

    const [
        isLoading,
        setIsLoading,
    ] = useState(true);

    const [
        hasError,
        setHasError,
    ] = useState(false);

    const [
        errorMessage,
        setErrorMessage,
    ] = useState("");

    const showMessage =
        appAPI?.showMessage;

    const colorStyle =
        createColorStyles(
            PRIMARY_COLOR,
        );

    useEffect(() => {
        if (
            userId === null
            || userId === undefined
            || userId === ""
        ) {
            setImprint("");
            setHasError(true);
            setErrorMessage(
                MESSAGE.missingUserId,
            );
            setIsLoading(false);

            showMessage?.(
                MESSAGE.missingUserId,
            );

            return undefined;
        }

        let requestIsActive =
            true;

        setImprint("");
        setHasError(false);
        setErrorMessage("");
        setIsLoading(true);

        let request;

        try {
            request =
                fetchSellerImprintAPI(
                    userId,
                );
        } catch (error) {
            const message = (
                error?.message
                ?? MESSAGE.loadError
            );

            setImprint("");
            setHasError(true);
            setErrorMessage(message);
            setIsLoading(false);

            showMessage?.(message);

            return undefined;
        }

        handleResponse(
            request,

            (response) => {
                if (
                    !requestIsActive
                ) {
                    return;
                }

                setImprint(
                    normalizeImprint(
                        response,
                    ),
                );

                setHasError(false);
                setErrorMessage("");
                setIsLoading(false);
            },

            (message) => {
                if (
                    !requestIsActive
                ) {
                    return;
                }

                const normalizedMessage = (
                    message
                    ?? MESSAGE.loadError
                );

                setImprint("");
                setHasError(true);
                setErrorMessage(
                    normalizedMessage,
                );
                setIsLoading(false);

                showMessage?.(
                    normalizedMessage,
                );
            },
        );

        return () => {
            requestIsActive =
                false;
        };
    }, [
        showMessage,
        userId,
    ]);

    const hasImprint =
        imprint.length > 0;

    return (
        <Paper
            component="main"
            elevation={0}
            sx={STYLE.paper}
        >
            {isLoading && (
                <LinearProgress
                    sx={
                        colorStyle
                            .progress
                    }
                />
            )}

            <Box
                sx={STYLE.content}
            >
                <Box
                    sx={STYLE.heading}
                >
                    <PersonOutlineIcon
                        sx={
                            colorStyle
                                .titleIcon
                        }
                    />

                    <Box
                        sx={
                            STYLE
                                .headingText
                        }
                    >
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={
                                colorStyle
                                    .title
                            }
                        >
                            {LABEL.title}
                        </Typography>

                        <Typography
                            variant="body1"
                            component="p"
                            sx={
                                STYLE.subtitle
                            }
                        >
                            {LABEL.subtitle}
                        </Typography>
                    </Box>
                </Box>

                <Divider
                    sx={STYLE.divider}
                />

                {!isLoading
                    && hasError
                    && (
                        <Alert
                            severity="error"
                        >
                            {errorMessage
                                || MESSAGE.loadError}
                        </Alert>
                    )}

                {!isLoading
                    && !hasError
                    && hasImprint
                    && (
                        <Typography
                            component="div"
                            variant="body1"
                            sx={
                                STYLE
                                    .imprintText
                            }
                        >
                            {imprint}
                        </Typography>
                    )}

                {!isLoading
                    && !hasError
                    && !hasImprint
                    && (
                        <Typography
                            component="p"
                            variant="body1"
                            sx={
                                STYLE
                                    .emptyText
                            }
                        >
                            {
                                MESSAGE
                                    .noImprint
                            }
                        </Typography>
                    )}
            </Box>
        </Paper>
    );
}